import { createElement, useEffect, useState } from 'react';
import { Linkedin, Facebook, Instagram, Twitter, Youtube, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin,
  youtube: Youtube,
};

/**
 * Hierarchy tree-view showing the editorial team (only when admin enables
 * the widget in /admin/imprint). Each node renders only picture + name +
 * position with social-link icons, joined by connector lines so the team
 * org-chart reads top-down.
 */
export default function ImprintTreeWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}/public/imprint/tree`).then(r => r.ok ? r.json() : null).then(setData).catch(() => {});
  }, []);
  if (!data?.enabled || !(data.tree || []).length) return null;
  return (
    <section className="my-10" data-testid="imprint-tree-widget">
      <h2 className="text-xl font-bold mb-1 text-gray-900 dark:text-gray-100">{data.headline}</h2>
      {data.intro && <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">{data.intro}</p>}
      <div className="overflow-x-auto pb-3">
        <ul className="tree-root flex justify-center gap-8 min-w-max">
          {data.tree.map(node => createElement(TreeNode, { key: node.id, node }))}
        </ul>
      </div>
      <style>{`
        .tree-root, .tree-children { list-style: none; padding: 0; margin: 0; }
        .tree-children { display: flex; gap: 1.5rem; padding-top: 2rem; position: relative; }
        .tree-children::before {
          content: ''; position: absolute; top: 0; left: 50%; height: 1.25rem;
          border-left: 1px solid rgb(148 163 184 / 0.4);
        }
        .tree-children::after {
          content: ''; position: absolute; top: 1.25rem; left: 0; right: 0;
          height: 1px; background: rgb(148 163 184 / 0.4);
        }
        .tree-children > li { position: relative; }
        .tree-children > li::before {
          content: ''; position: absolute; top: -0.75rem; left: 50%; height: 0.75rem;
          border-left: 1px solid rgb(148 163 184 / 0.4);
        }
        .tree-children > li:only-child::before { display: block; }
        .tree-children > li:first-child::after,
        .tree-children > li:last-child::after { content: ''; position: absolute; top: -1px;
          height: 2px; width: 50%; background: rgb(15 23 42); }
        .tree-children > li:first-child::after { left: 0; }
        .tree-children > li:last-child::after { right: 0; }
        .dark .tree-children > li:first-child::after,
        .dark .tree-children > li:last-child::after { background: rgb(11 18 32); }
        .light-bg .tree-children > li:first-child::after,
        .light-bg .tree-children > li:last-child::after { background: #fff; }
      `}</style>
    </section>
  );
}

function TreeNode({ node }) {
  const social = node.social_links || {};
  return (
    <li className="flex flex-col items-center" data-testid={`imprint-node-${node.id}`}>
      <div className="bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-700 rounded-2xl px-4 py-3 shadow-sm min-w-[180px] text-center">
        {node.picture ? (
          <img src={node.picture} alt="" className="w-16 h-16 rounded-full object-cover mx-auto mb-2 border-2 border-white dark:border-slate-700 shadow" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-slate-200 dark:bg-slate-700 mx-auto mb-2" />
        )}
        <p className="text-sm font-bold text-slate-900 dark:text-slate-100 leading-tight">{node.name}</p>
        {(node.position || node.role) && (
          <p className="text-[11px] uppercase tracking-widest text-red-600 dark:text-red-400 mt-0.5 font-bold">
            {node.position || node.role}
          </p>
        )}
        {Object.keys(social).filter(k => social[k]).length > 0 && (
          <div className="flex justify-center gap-2 mt-2">
            {Object.entries(social).filter(([, v]) => v).map(([k, v]) => {
              const Icon = ICONS[k] || Globe;
              return (
                <a key={k} href={v} target="_blank" rel="noopener noreferrer"
                   data-testid={`imprint-node-${node.id}-social-${k}`}
                   className="text-slate-500 hover:text-red-600 transition-colors">
                  <Icon size={14}/>
                </a>
              );
            })}
          </div>
        )}
      </div>
      {(node.children || []).length > 0 && (
        <ul className="tree-children">
          {node.children.map(c => createElement(TreeNode, { key: c.id, node: c }))}
        </ul>
      )}
    </li>
  );
}
